import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { LOCATION_GROUPS, MAPS } from "../data/site";
import { Reveal, SectionHeading } from "./Reveal";

export const LocationSection = () => (
  <section id="location" data-testid="location-section" className="py-20 sm:py-28 bg-[#FFFFFF]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading index="06" eyebrow="Connectivity" title="Connected to New Town. Close to Everyday Essentials." />
      <Reveal delay={0.1}>
        <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
          The project sits at Hatishala, off and adjacent to Newtown Action Area III — minutes from the IT corridor,
          schools, hospitals and daily conveniences. Explore the full{" "}
          <Link to="/vinayak-21-acres-location" className="text-[#65A30D] font-semibold hover:underline">Vinayak 21 Acres location and connectivity</Link>{" "}
          guide for verified travel times and neighbourhood detail.
        </p>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-10 mt-14">
        <Reveal className="lg:col-span-5">
          <div
            data-testid="location-map-card"
            className="relative h-full min-h-[340px] rounded-2xl bg-[#FFFFFF] border border-[#E3EFCB] overflow-hidden flex flex-col shadow-[0_24px_48px_-28px_rgba(77,124,15,0.25)]"
          >
            <iframe
              title="Vinayak 21 Acres location map — Hatishala, off Newtown Action Area III, Kolkata"
              src={MAPS.embed}
              className="w-full flex-1 min-h-[280px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="location-map-embed"
            />
            <div className="p-5 flex items-center justify-between gap-4 border-t border-[#E3EFCB]">
              <div className="flex items-start gap-3 min-w-0">
                <span className="w-9 h-9 rounded-full bg-[#EAF5D6] border border-[#DCECC0] flex items-center justify-center shrink-0">
                  <MapPin size={15} className="text-[#65A30D]" />
                </span>
                <span className="leading-tight min-w-0">
                  <span className="block font-sub font-bold text-sm text-[#365314]">Hatishala, Kolkata</span>
                  <span className="block text-xs text-[#65A30D] mt-0.5">Adjacent to Newtown Action Area III · map indicative of the Hatisala More area</span>
                </span>
              </div>
              <a
                href={MAPS.directions}
                target="_blank" rel="noopener noreferrer"
                data-testid="location-directions-link"
                className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-sub font-semibold text-[#3F6212] border border-[#9CCB58] rounded-full px-4 py-2 hover:bg-[#F4FAE8] transition-all duration-300"
              >
                Directions
              </a>
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {LOCATION_GROUPS.map((group, gi) => (
            <Reveal key={group.category} delay={0.06 * gi} className={group.category === "Education" ? "sm:col-span-2" : ""}>
              <div className="bg-white border border-[#DCECC0] rounded-xl p-6 h-full" data-testid={`location-group-${group.category.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="overline-tag mb-4">{group.category}</p>
                <ul className={`divide-y divide-[#E3EFCB] ${group.category === "Education" ? "sm:columns-2 sm:gap-8" : ""}`}>
                  {group.items.map((item) => (
                    <li key={item.name} className="flex items-center justify-between py-2 break-inside-avoid">
                      <span className="text-[13px] text-[#4D7C0F]">{item.name}</span>
                      <span className="font-mono-spec text-[12px] text-[#365314] shrink-0 ml-3">{item.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.1}>
        <p className="text-xs text-[#65A30D] mt-8" data-testid="travel-time-disclaimer">
          Travel times are indicative and may vary depending on traffic and route.
        </p>
        <div className="mt-5 flex flex-wrap gap-3" data-testid="location-aa3-link-wrap">
          <Link
            to="/properties-in-new-town-action-area-3"
            data-testid="location-aa3-link"
            className="inline-flex items-center gap-2 text-[#365314] text-sm font-sub font-semibold border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300"
          >
            Properties Near Newtown Action Area III
          </Link>
          <Link
            to="/flats-near-new-town-action-area-3"
            data-testid="location-flats-link"
            className="inline-flex items-center gap-2 text-[#365314] text-sm font-sub font-semibold border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300"
          >
            Flats Near Newtown Action Area III
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
